import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ZipCodeInput } from "@/components/ZipCodeInput";
import { RadiusSelector } from "@/components/RadiusSelector";
import { useUserLocation } from "@/hooks/useUserLocation";
import { MapPin, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface LocationStepProps {
  onNext: () => void;
}

export const LocationStep = ({ onNext }: LocationStepProps) => {
  const { toast } = useToast();
  const { location, updateLocation, isUpdating } = useUserLocation();
  
  const [zipCode, setZipCode] = useState(location?.home_zip_code || "");
  const [city, setCity] = useState(location?.home_city || "");
  const [state, setState] = useState(location?.home_state || "");
  const [lat, setLat] = useState<number | null>(location?.home_latitude || null);
  const [lon, setLon] = useState<number | null>(location?.home_longitude || null);
  const [radius, setRadius] = useState(location?.local_radius_miles || 200);

  const handleZipCodeChange = (
    newZipCode: string,
    newCity: string,
    newState: string,
    latitude: number,
    longitude: number
  ) => {
    setZipCode(newZipCode);
    setCity(newCity);
    setState(newState);
    setLat(latitude);
    setLon(longitude);
  };

  const handleSave = () => {
    if (lat && lon) {
      updateLocation({
        home_zip_code: zipCode,
        home_city: city,
        home_state: state,
        home_latitude: lat,
        home_longitude: lon,
        local_radius_miles: radius,
      });
      toast({
        title: "Location saved",
        description: `Your home location is set to ${city}, ${state}`,
      });
    }
    onNext();
  };

  const handleSkip = () => {
    onNext();
  };

  const hasLocation = lat !== null && lon !== null;

  return (
    <Card>
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
          <MapPin className="w-6 h-6 text-primary" />
        </div>
        <CardTitle className="text-2xl">Set Your Home Location</CardTitle>
        <CardDescription>
          This helps us show you trails and activity in your area. You can update this anytime in Settings.
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <ZipCodeInput
            value={zipCode}
            city={city}
            state={state}
            onChange={handleZipCodeChange}
          />
          
          {hasLocation && (
            <div className="space-y-2">
              <RadiusSelector value={radius} onChange={setRadius} />
            </div>
          )}
        </div>

        <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
          <p className="font-medium mb-2">Why set a home location?</p>
          <ul className="list-disc list-inside space-y-1">
            <li>See "Local" activity in your feed</li>
            <li>Center the trail map on your area</li>
            <li>Get personalized recommendations</li>
          </ul>
        </div>

        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={handleSkip}
            className="flex-1"
            size="lg"
          >
            Skip for Now
          </Button>
          <Button 
            onClick={handleSave}
            disabled={isUpdating}
            className="flex-1"
            size="lg"
          >
            {isUpdating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : hasLocation ? (
              "Save & Continue"
            ) : (
              "Continue"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
